import { useLang } from '../../i18n/LangContext';
import { useEffect, useState } from 'react';
import {
  companionCallNurse,
  companionCallNurseStatus,
  companionMe,
  companionMemory,
  companionMemoryForget,
  companionMemoryRestore,
  companionSharingSet,
  companionStarters,
  companionUnsafe,
  myDayIntent,
  toSecondPerson,
  type CompanionMemoryFact,
  greet,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { usePageTab } from '../../shell/Shell';
import { useMyDay } from '../home/useMyDay';
import { A, firstName, monthName, st, useApi } from './ui';
import { sendChat } from './chat';
import { FirstTalk, useFirstTalk } from './FirstTalk';

// The resident's own Atlas (design T1107-1330, isResidentRole): big type, three
// tabs (Talk, My day, About me), help always above them, and the first talk until it is
// done. Everything the resident does is a real call: the chat is POST /chat, My day is
// the my-day routes, the first talk is /residents/:id/onboarding.

const FONT = [19, 22, 25, 29];
const SIZES = [16, 19, 23, 27];
const BODY = [16, 19, 22, 26];
const PROMPTS = ['What is on my day?', 'How are you feeling today?', 'Tell me a happy memory', 'Let’s play a word game', 'Read me some good news'];
// Who Atlas offers to get on the phone comes from the care circle (familyContact on
// /companion/me): "your daughter Lucia", or "your family" when no one is named.
const refusal = (who: string | null | undefined): [string, string][] => [
  ['If you ask me about a dose or a symptom', `I will not answer it myself. I will say so, and I will offer to get ${who || 'your family'} or the nurse on the phone right then.`],
  ['If I get something wrong', 'Tell me and I will fix it. Say “that is not right” and I will take it back out.'],
  ['If you change your mind', 'Anything you ask me to forget can be undone straight away.'],
];
type Voice = 'idle' | 'listening' | 'thinking' | 'speaking' | 'denied';
const VL: Record<Voice, string> = { idle: 'Talk to me', listening: 'Listening. Say anything.', thinking: 'One moment…', speaking: 'Speaking. Tap to stop.', denied: 'Talk to me' };
const VB: Record<Voice, string> = { idle: '#1D3F6B', listening: '#1F6B52', thinking: '#3C454F', speaking: '#0F6B63', denied: '#1D3F6B' };

const initialsOf = (n: string) =>
  n
    .trim()
    .split(/\s+/)
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

function MyDayTab() {
  // The resident's own day: no personId, so the server reads it from the session.
  const md = useMyDay({ personId: null, variant: 'resident' });
  // An older adult's screen shows what is next, not the whole day (design V4 mdLayer): up
  // to four open items in time order, done items folded away, "Later today" opens the rest.
  const [allOn, setAllOn] = useState(false);
  if (md.loading) return null;
  const openL = md.items.filter((d) => !d.isDone);
  const doneN = md.items.length - openL.length;
  const shown = allOn ? md.items : openL.slice(0, 4);
  const hasMore = md.items.length > shown.length || allOn;
  const moreLabel = allOn ? 'Show less' : 'Later today · ' + (openL.length - shown.length) + ' more' + (doneN ? ' · ' + doneN + ' done' : '') + '  →';
  if (md.error && !md.items.length) {
    return <div style={st('font-size:17px;color:#3C454F;line-height:1.5;')}>Your day could not be loaded just now. Ask Atlas “what is on my day?”, or try again in a moment.</div>;
  }
  return (
    <div data-screen-label="My day (resident)" style={st('box-sizing:border-box;min-width:0;animation:riseIn .46s cubic-bezier(.22,.61,.36,1);animation-delay:90ms;')}>
      <div style={st('display:flex;align-items:baseline;gap:6px 14px;flex-wrap:wrap;min-width:0;')}>
        <span style={st('font-size:14px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#22272E;')}>My day</span>
        <span style={st('font-size:17px;font-weight:700;color:#1F6B52;font-variant-numeric:tabular-nums;')}>{md.progress}</span>
        <span style={st('font-size:15px;color:#54606C;')}>{md.streak ?? ''}</span>
        <span style={st('flex:1 1 10px;')} />
        <span style={st('font-size:15px;color:#54606C;')}>Or tell Atlas “I did it”</span>
      </div>
      <div style={st('height:8px;border-radius:4px;background:rgba(34,39,46,.08);margin:10px 0 4px;overflow:hidden;')}>
        <div style={st(`height:100%;border-radius:4px;background:#1F6B52;width:${md.pct};transition:width .5s cubic-bezier(.22,.61,.36,1);`)} />
      </div>
      {shown.map((d) => (
        <div key={d.id} style={st(d.rowStyle)}>
          <span style={st('flex:0 0 64px;font-size:16px;font-weight:700;color:#54606C;font-variant-numeric:tabular-nums;white-space:nowrap;')}>{d.time}</span>
          <span style={st('flex:1 1 220px;min-width:0;display:flex;flex-direction:column;gap:3px;')}>
            <span style={st('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;')}>
              <span style={st('font-size:19px;font-weight:700;color:#22272E;line-height:1.3;')}>{d.label}</span>
              <span style={st(d.tagStyle)}>{d.tag}</span>
            </span>
            <span style={st('font-size:15px;color:#54606C;line-height:1.4;')}>{d.why}</span>
          </span>
          <span style={st('flex:0 0 auto;display:flex;align-items:center;gap:8px;flex-wrap:wrap;justify-content:flex-end;')}>
            {d.isWater && (
              <>
                <span style={st('display:inline-flex;align-items:center;gap:4px;')} aria-label={d.waterText}>
                  {d.glasses.map((g, k) => (
                    <span key={k} style={st(g)} />
                  ))}
                </span>
                <span style={st('font-size:15px;font-weight:700;color:#264C7E;font-variant-numeric:tabular-nums;white-space:nowrap;')}>{d.waterText}</span>
                <A write onClick={d.onGlass} s="display:inline-flex;align-items:center;justify-content:center;min-height:52px;padding:0 18px;border-radius:12px;font-size:17px;font-weight:700;color:#fff;background:#2F5C97;text-decoration:none;white-space:nowrap;">
                  + A glass
                </A>
              </>
            )}
            {d.canDo && (
              <>
                <A write onClick={d.onHelp} s="display:inline-flex;align-items:center;justify-content:center;min-height:52px;padding:0 16px;border-radius:12px;font-size:16px;font-weight:700;color:#264C7E;border:1.5px solid rgba(47,92,151,.45);text-decoration:none;white-space:nowrap;">
                  With help
                </A>
                <A write onClick={d.onDo} s="display:inline-flex;align-items:center;justify-content:center;min-height:52px;padding:0 20px;border-radius:12px;font-size:17px;font-weight:700;color:#fff;background:#1F6B52;text-decoration:none;white-space:nowrap;">
                  I did it
                </A>
              </>
            )}
            {d.isDone && (
              <>
                <span style={st('font-size:17px;font-weight:700;color:#1F6B52;white-space:nowrap;')}>
                  ✓ <span>{d.doneText}</span>
                </span>
                <A write onClick={d.onUndo} s="display:inline-flex;align-items:center;min-height:44px;padding:0 6px;font-size:15px;font-weight:700;color:#2F5C97;text-decoration:none;">
                  Undo
                </A>
              </>
            )}
          </span>
        </div>
      ))}
      {hasMore && (
        <A onClick={() => setAllOn((v) => !v)} s="display:flex;align-items:center;gap:10px;min-height:60px;padding:0 4px;border-top:1px solid rgba(34,39,46,.08);font-size:19px;font-weight:700;color:#2F5C97;text-decoration:none;">
          {moreLabel}
        </A>
      )}
      {md.suggestions.map((x) => (
        <div key={x.id} style={st('display:flex;align-items:center;gap:10px 16px;flex-wrap:wrap;min-width:0;padding:12px 14px;margin-top:10px;border-radius:12px;background:rgba(47,92,151,.07);')}>
          <span style={st('flex:1 1 240px;min-width:0;font-size:17px;color:#22272E;line-height:1.4;')}>
            <b>{x.from}</b> suggests: <span>{x.what}</span>
          </span>
          <A write onClick={x.onNo} s="display:inline-flex;align-items:center;min-height:52px;padding:0 16px;border-radius:12px;font-size:16px;font-weight:700;color:#3C454F;border:1.5px solid rgba(34,39,46,.25);text-decoration:none;">
            Not now
          </A>
          <A write onClick={x.onYes} s="display:inline-flex;align-items:center;min-height:52px;padding:0 18px;border-radius:12px;font-size:17px;font-weight:700;color:#fff;background:#2F5C97;text-decoration:none;">
            Add to my day
          </A>
        </div>
      ))}
      <div style={st('display:flex;align-items:center;gap:10px;flex-wrap:wrap;min-width:0;margin-top:12px;')}>
        <input
          value={md.draft}
          onChange={(e) => md.setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              md.add();
            }
          }}
          placeholder="Add something for today, like Water the plant"
          aria-label="Add something for today"
          style={st('flex:1 1 260px;min-width:0;min-height:52px;box-sizing:border-box;padding:0 16px;border-radius:12px;border:1.5px solid rgba(34,39,46,.2);background:#FCFAF4;font-family:Archivo,system-ui,sans-serif;font-size:17px;color:#22272E;')}
        />
        {/* Says what is missing instead of a click that does nothing (QA-16). */}
        <A write onClick={md.add} disabledReason={md.draft.trim() ? null : 'Write what to add first.'} s="display:inline-flex;align-items:center;min-height:52px;padding:0 20px;border-radius:12px;font-size:17px;font-weight:700;color:#FCFAF4;background:#22272E;text-decoration:none;">
          Add
        </A>
      </div>
      <div style={st('font-size:15px;color:#54606C;line-height:1.5;margin-top:10px;text-wrap:pretty;')}>{md.privacy}</div>
    </div>
  );
}

function AboutMe({ residentId, fullName, ts, hc }: { residentId: string; fullName: string; ts: number; hc: boolean }) {
  const { client } = useSession();
  const mem = useApi((c, signal) => companionMemory(c, { params: { id: residentId }, signal }), [residentId]);
  const me = useApi((c, signal) => companionMe(c, { signal }));
  const [last, setLast] = useState<CompanionMemoryFact | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const card = hc ? 'background:#fff;border:2px solid #22272E;border-radius:12px;padding:18px;' : 'padding:16px 0;border-top:1px solid rgba(34,39,46,.16);';
  const body = `font-size:${BODY[ts]}px;line-height:1.7;max-width:62ch;${hc ? 'color:#000;' : 'color:#3C454F;'}`;
  const facts = mem.data?.facts ?? [];
  const since = monthName(mem.data?.since);
  const forget = async (f: CompanionMemoryFact) => {
    setErr(null);
    try {
      await companionMemoryForget(client, { params: { id: residentId, factId: f.id } });
      mem.setData((p) => (p ? { ...p, facts: p.facts.filter((x) => x.id !== f.id) } : p));
      setLast(f);
    } catch (e) {
      setErr((e as Error).message || 'I could not forget that just now.');
    }
  };
  const undo = async () => {
    if (!last) return;
    setErr(null);
    try {
      await companionMemoryRestore(client, { params: { id: residentId, factId: last.id } });
      setLast(null);
      mem.reload();
    } catch (e) {
      setErr((e as Error).message || 'I could not put it back just now.');
    }
  };
  const toggle = async (personId: string | null, on: boolean) => {
    if (!personId) return;
    setErr(null);
    try {
      await companionSharingSet(client, { body: { personId, on } });
      me.setData((p) => (p ? { ...p, sharing: p.sharing.map((x) => (x.personId === personId ? { ...x, on } : x)) } : p));
    } catch (e) {
      setErr((e as Error).message || 'That did not change. Try again.');
    }
  };
  return (
    <div style={st('display:flex;flex-direction:column;gap:14px;min-width:0;')}>
      {last && (
        <div style={st('display:flex;align-items:center;gap:14px;flex-wrap:wrap;row-gap:8px;margin-top:20px;padding:14px 16px;border-radius:11px;background:rgba(47,138,111,.10);')}>
          <span style={st('flex:1 1 200px;min-width:0;font-size:17px;color:#12503C;line-height:1.4;text-wrap:pretty;')}>{`I have forgotten this: ${toSecondPerson(last.name)}.`}</span>
          <A write onClick={() => void undo()} s="flex:0 0 auto;display:inline-flex;align-items:center;min-height:44px;padding:0 18px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#fff;background:#1F6B52;">
            Put it back
          </A>
          <A onClick={() => setLast(null)} s="flex:0 0 auto;display:inline-flex;align-items:center;min-height:44px;padding:0 16px;border-radius:9px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;color:#3C454F;background:rgba(34,39,46,.07);">
            That is right
          </A>
        </div>
      )}
      <div style={st(card)}>
        <div style={st('display:flex;align-items:center;gap:13px;flex-wrap:wrap;row-gap:6px;')}>
          <span style={st('display:flex;align-items:center;justify-content:center;width:46px;height:46px;flex:0 0 46px;border-radius:50%;background:#2F5C97;color:#fff;font-size:15px;font-weight:700;')}>
            {initialsOf(fullName)}
          </span>
          <span>
            <span style={st('display:block;font-size:22px;font-weight:800;letter-spacing:-.02em;')}>Your Atlas</span>
            <span style={st('display:block;font-size:17px;color:#3C454F;')}>{since ? `With you since ${since}` : 'Just getting to know you'}</span>
          </span>
        </div>
      </div>
      <div style={st(hc ? 'background:#fff;border:2px solid #22272E;border-radius:12px;padding:18px;font-size:16px;color:#000;line-height:1.55;' : 'padding:14px 0;border-top:1px solid rgba(34,39,46,.16);font-size:16px;color:#3C454F;line-height:1.55;')}>
        Everything Atlas remembers about you stays private to you and your care circle. You can ask it to forget anything, anytime.
      </div>
      <div style={st(card)}>
        <div style={st('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#54606C" strokeWidth="1.8" style={st('flex:0 0 18px;')} aria-hidden="true">
            <path d="M12 20.3S3.8 15.4 3.8 9.7A4.4 4.4 0 0 1 12 7.3a4.4 4.4 0 0 1 8.2 2.4c0 5.7-8.2 10.6-8.2 10.6Z" />
          </svg>
          <span style={st('font-size:20px;font-weight:800;letter-spacing:-.015em;')}>What I remember about you</span>
        </div>
        <div style={st(`${body}margin-top:10px;`)}>Carried into every conversation, so you never have to repeat yourself.</div>
        <div style={st('margin-top:12px;')}>
          {mem.data && facts.length === 0 && (
            <div style={st('margin-top:12px;padding-top:12px;border-top:1px solid rgba(34,39,46,.08);font-size:15px;color:#54606C;line-height:1.5;')}>
              Nothing remembered yet. Atlas will add what you tell it, and you can ask it to forget anything.
            </div>
          )}
          {facts.map((m) => (
            <div key={m.id} style={st('display:flex;align-items:baseline;gap:12px;flex-wrap:wrap;row-gap:4px;padding:11px 0;border-top:1px solid rgba(34,39,46,.12);min-width:0;')}>
              <span style={st('flex:1 1 150px;min-width:0;')}>
                {/* The memory's name is stored for the care team in the third person; on the resident's
                    own screen it speaks to them. The free-text detail is left as written: its "her"
                    may be a motorcycle, not the resident. */}
                <span style={st('display:block;font-size:17px;font-weight:700;line-height:1.3;')}>{toSecondPerson(m.name)}</span>
                <span style={st('display:block;font-size:15px;color:#54606C;line-height:1.45;margin-top:2px;text-wrap:pretty;')}>{m.detail}</span>
              </span>
              <A write onClick={() => void forget(m)} s="flex:0 0 auto;display:inline-flex;align-items:center;min-height:44px;padding:0 14px;border-radius:9px;font-size:15px;font-weight:700;text-decoration:none;white-space:nowrap;color:#8A1D17;background:rgba(179,38,30,.10);">
                Forget this
              </A>
            </div>
          ))}
        </div>
      </div>
      <div style={st(card)}>
        <div style={st('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#264C7E" strokeWidth="1.8" aria-hidden="true">
            <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
            <circle cx="12" cy="12" r="2.6" />
          </svg>
          <span style={st('font-size:20px;font-weight:800;letter-spacing:-.015em;')}>Who can see this</span>
        </div>
        <div style={st(`${body}margin-top:10px;`)}>
          Nothing is shared until you say so. Turn a person on and they see what is listed. Turn them off and they stop, straight away, and nobody is told you did it.
        </div>
        <div style={st('margin-top:12px;')}>
          {(me.data?.sharing ?? []).map((p) => (
            <div key={p.name} style={st('display:flex;align-items:center;gap:14px;flex-wrap:wrap;row-gap:10px;padding:13px 0;border-top:1px solid rgba(34,39,46,.08);')}>
              <span style={st('flex:1 1 190px;min-width:min(100%,190px);')}>
                <span style={st('display:block;font-size:18px;font-weight:700;')}>{p.name}</span>
                <span style={st('display:block;font-size:16px;color:#3C454F;line-height:1.45;margin-top:3px;text-wrap:pretty;')}>{p.sees}</span>
              </span>
              {/* The care team always sees this: a status, not a button that does nothing. */}
              {p.fixed ? (
                <span data-testid="sharing-always-on" style={st('display:flex;align-items:center;justify-content:center;min-width:96px;min-height:52px;padding:0 18px;border-radius:12px;font-size:17px;font-weight:700;white-space:nowrap;flex:0 0 auto;background:rgba(34,39,46,.06);color:#54606C;')}>
                  Always on
                </span>
              ) : (
                <A
                  write
                  onClick={() => void toggle(p.personId, !p.on)}
                  s={`display:flex;align-items:center;justify-content:center;min-width:96px;min-height:52px;padding:0 18px;border-radius:12px;font-size:17px;font-weight:700;text-decoration:none;white-space:nowrap;flex:0 0 auto;${p.on ? 'background:rgba(47,138,111,.2);color:#12503C;border:1px solid rgba(47,138,111,.4);' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.22);'}`}
                >
                  {p.on ? 'On' : 'Off'}
                </A>
              )}
            </div>
          ))}
        </div>
        <div style={st('font-size:16px;color:#3C454F;line-height:1.5;margin-top:14px;padding-top:12px;border-top:1px solid rgba(34,39,46,.08);text-wrap:pretty;')}>
          The one thing it will not keep to itself is a serious worry about your safety. If you say something that suggests you are unwell or at risk, a person here is told.
        </div>
      </div>
      {err && <div role="alert" style={st('font-size:17px;color:#8A1C16;')}>{err}</div>}
      <div style={st('margin-top:24px;')}>
        {refusal(me.data?.familyContact).map(([k, v]) => (
          <div key={k} style={st('padding:12px 0;border-top:1px solid rgba(34,39,46,.14);min-width:0;')}>
            <span style={st('display:block;font-size:17px;font-weight:700;line-height:1.35;text-wrap:pretty;')}>{k}</span>
            <span style={st('display:block;font-size:16px;color:#3C454F;line-height:1.5;margin-top:3px;text-wrap:pretty;')}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ResidentCompanion() {
  const { client, session } = useSession();
  const { lang } = useLang();
  const residentId = session?.user.id ?? '';
  const orgId = session?.user.organizationId ?? '';
  const fullName = session?.user.name ?? '';
  const [tab, setTab] = useState<'talk' | 'day' | 'me'>('talk');
  const [ts, setTs] = useState(1);
  const [hc, setHc] = useState(false);
  const [nurse, setNurse] = useState<'idle' | 'sending' | 'called'>('idle');
  const [unsafe, setUnsafe] = useState(false);
  const [turns, setTurns] = useState<{ me: boolean; text: string }[]>([]);
  const [draft, setDraft] = useState('');
  const [voice, setVoice] = useState<Voice>('idle');
  const [aloud, setAloud] = useState(true);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const firstTalk = useFirstTalk(residentId || null);
  const starters = useApi((c, signal) => companionStarters(c, { params: { id: residentId }, signal }), [residentId], !!residentId);
  usePageTab(tab === 'talk' ? 'Talk' : tab === 'day' ? 'My day' : 'About me');
  const name = starters.data?.starters.preferredName || firstName(fullName);

  useEffect(() => {
    if (voice === 'listening' || voice === 'idle' || voice === 'denied') return;
    // Thinking then speaking are the device assistant's; the page only mirrors them.
    const t = window.setTimeout(() => setVoice(voice === 'thinking' ? 'speaking' : 'idle'), voice === 'thinking' ? 1400 : 3800);
    return () => window.clearTimeout(t);
  }, [voice]);

  const say = async (text: string) => {
    const t = text.trim();
    if (!t || !residentId) return;
    setDraft('');
    setErr(null);
    setTurns((p) => [...p, { me: true, text: t }]);
    try {
      // My day first: "I took my pills" is a tap said out loud. Anything else is a conversation.
      const d = await myDayIntent(client, { body: { utterance: t } }).catch(() => null);
      let reply: string;
      if (d && d.action !== 'UNKNOWN') reply = d.reply;
      else {
        const r = await sendChat(client, { message: t, userId: residentId, organizationId: orgId, conversationId });
        if (r.conversationId) setConversationId(r.conversationId);
        reply = r.text;
      }
      setTurns((p) => [...p, { me: false, text: reply }]);
    } catch (e) {
      setErr((e as Error).message || 'I could not answer just now. Press Call a nurse if you need someone.');
    }
  };

  const callNurse = async () => {
    if (nurse !== 'idle') return;
    setNurse('sending');
    try {
      await companionCallNurse(client, { params: { id: residentId } });
      setNurse('called');
    } catch (e) {
      setNurse('idle');
      setErr((e as Error).message || 'The nurse call did not go through. Say “Hey Siri, call my nurse”, or ask anyone nearby.');
    }
  };
  const feelUnsafe = async () => {
    setErr(null);
    try {
      await companionUnsafe(client, { body: {} });
      setUnsafe(true);
    } catch (e) {
      setErr((e as Error).message || 'That did not go through. Press Call a nurse, or say it out loud to me.');
    }
  };
  const toggleVoice = () => {
    if (voice === 'listening') return setVoice('thinking');
    if (voice === 'speaking' || voice === 'thinking') return setVoice('idle');
    const start = () => setVoice('listening');
    try {
      navigator.permissions
        .query({ name: 'microphone' as PermissionName })
        .then((p) => (p.state === 'denied' ? setVoice('denied') : start()), start);
    } catch {
      start();
    }
  };

  const called = nurse === 'called';
  // "Your nurse has been told" until a member of staff acknowledges it on their phone, then
  // "A nurse is coming" (GET /residents/:id/call-nurse; safety audit 2026-10-04).
  const callStatus = useApi((c, signal) => companionCallNurseStatus(c, { params: { id: residentId }, signal }), [residentId, called], called && !!residentId, 8000);
  const coming = called && callStatus.data?.status === 'ACKNOWLEDGED';
  const vOn = voice === 'listening' || voice === 'speaking';
  const helloStyle = `box-sizing:border-box;max-width:46ch;border-radius:18px 18px 18px 4px;padding:18px 20px;line-height:1.5;text-wrap:pretty;font-size:${FONT[ts]}px;${hc ? 'background:#fff;border:2px solid #000;color:#000;' : 'background:#FCFAF4;border:1px solid rgba(34,39,46,.14);color:#22272E;'}`;

  return (
    <div data-testid="companion-resident" style={st(`flex:0 0 auto;${hc ? 'background:#fff;color:#000;padding:4px 10px 12px;border-radius:12px;' : ''}`)}>
      <div style={st('box-sizing:border-box;max-width:860px;margin:0 auto;display:flex;flex-direction:column;gap:18px;min-width:0;')}>
        <div style={st('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:10px;')}>
          <span style={st('font-size:19px;font-weight:700;')}>Text size</span>
          {[0, 1, 2, 3].map((i) => (
            <A
              key={i}
              label={['Small', 'Medium', 'Large', 'Largest'][i] + ' text'}
              selected={ts === i}
              onClick={() => setTs(i)}
              s={`display:inline-flex;align-items:center;justify-content:center;width:52px;height:52px;border-radius:10px;text-decoration:none;font-weight:700;box-sizing:border-box;white-space:nowrap;font-size:${SIZES[i]}px;${ts === i ? 'background:#1D3F6B;color:#fff;border:2px solid #1D3F6B;' : 'background:#FCFAF4;color:#22272E;border:2px solid rgba(34,39,46,.3);'}`}
            >
              A
            </A>
          ))}
          <A
            onClick={() => setHc(!hc)}
            selected={hc}
            s={`display:inline-flex;align-items:center;gap:9px;min-height:48px;padding:0 18px;border-radius:9px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;${hc ? 'background:#22272E;color:#fff;border:1px solid #22272E;' : 'background:#FCFAF4;color:#22272E;border:1px solid rgba(34,39,46,.18);'}`}
            hover="border-color:rgba(47,92,151,.45);"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 3v18a9 9 0 0 0 0-18Z" fill="currentColor" />
            </svg>
            High contrast
          </A>
          <span style={st('flex:1 1 10px;')} />
          {/* Once the nurse is told, it says so as a status; pressing it again did nothing. */}
          {called ? (
            <span role="status" data-testid="nurse-status" style={st('flex:0 0 auto;display:inline-flex;align-items:center;gap:10px;min-height:60px;padding:0 24px;border-radius:14px;font-size:20px;font-weight:800;white-space:nowrap;color:#fff;background:#1F6B52;')}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
              {coming ? 'Nurse is coming' : 'Nurse told'}
            </span>
          ) : (
            <A write onClick={() => void callNurse()} s="flex:0 0 auto;display:inline-flex;align-items:center;gap:10px;min-height:60px;padding:0 24px;border-radius:14px;font-size:20px;font-weight:800;text-decoration:none;white-space:nowrap;color:#fff;background:#B3261E;">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Call a nurse
            </A>
          )}
        </div>
        {!unsafe && (
          <A write onClick={() => void feelUnsafe()} s="align-self:flex-start;display:inline-flex;align-items:center;gap:12px;min-height:60px;padding:0 22px;border-radius:12px;font-size:20px;font-weight:700;text-decoration:none;color:#8A1C16;background:#FCFAF4;border:2px solid rgba(179,38,30,.35);box-sizing:border-box;">
            I don’t feel safe
          </A>
        )}
        {unsafe && (
          <div role="status" style={st('display:flex;flex-direction:column;gap:8px;padding:18px 20px;border-radius:14px;background:#FFF4F2;border:2px solid rgba(179,38,30,.35);color:#22272E;')}>
            <span style={st('font-size:22px;font-weight:800;line-height:1.3;')}>Thank you for telling me.</span>
            <span style={st('font-size:19px;line-height:1.5;text-wrap:pretty;')}>
              Someone you can trust will come and talk with you, in private, within the hour. It will not be anyone you are worried about. You can also say it out loud to me, or press Call a nurse.
            </span>
          </div>
        )}
        {called && <div style={st('padding:16px 18px;border-radius:14px;background:rgba(47,138,111,.12);font-size:20px;font-weight:700;color:#12503C;line-height:1.4;')}>{coming ? 'A nurse is coming. Stay where you are.' : 'Your nurse has been told. Stay where you are.'}</div>}

        <FirstTalk residentId={residentId} fullName={fullName} q={firstTalk} onBigger={() => setTs(3)} />

        <div role="tablist" aria-label="Atlas" style={st('display:flex;gap:10px;min-width:0;')}>
          {(
            [
              ['talk', 'Talk'],
              ['day', 'My day'],
              ['me', 'About me'],
            ] as const
          ).map(([id, label]) => (
            <A
              key={id}
              role="tab"
              selected={tab === id}
              onClick={() => setTab(id)}
              s={`flex:1 1 0;display:inline-flex;align-items:center;justify-content:center;min-height:60px;padding:0 14px;border-radius:12px;font-size:20px;font-weight:700;text-decoration:none;white-space:nowrap;box-sizing:border-box;${tab === id ? 'background:#1D3F6B;color:#fff;' : hc ? 'background:#fff;color:#000;border:2px solid #000;' : 'background:#FCFAF4;color:#22272E;border:2px solid rgba(34,39,46,.22);'}`}
            >
              {label}
            </A>
          ))}
        </div>

        {tab === 'talk' && (
          <div style={st('display:flex;flex-direction:column;gap:16px;min-width:0;')}>
            <div style={st('display:flex;flex-direction:column;gap:14px;min-width:0;')}>
              <div style={st('display:flex;align-items:flex-end;gap:12px;min-width:0;')}>
                <span style={st('display:flex;align-items:center;justify-content:center;width:52px;height:52px;flex:0 0 52px;border-radius:50%;background:#2F5C97;color:#fff;')}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4L3 21l1.1-4.2A8.4 8.4 0 1 1 21 11.5Z" />
                  </svg>
                </span>
                <div style={st(helloStyle)}>{`${greet(name, new Date(), session?.user.timeZone ?? null, lang === 'es' ? 'es' : 'en')}.`}{' '}{'I am Atlas. I am here to chat, help you remember things, and get your nurse whenever you need one.'}</div>
              </div>
              {turns.map((m, i) => (
                <div key={i} style={st(`display:flex;flex:0 0 auto;${m.me ? 'justify-content:flex-end;' : 'padding-left:64px;'}`)}>
                  <div
                    style={st(
                      `box-sizing:border-box;max-width:46ch;border-radius:${m.me ? '18px 18px 4px 18px' : '18px 18px 18px 4px'};padding:16px 20px;line-height:1.5;text-wrap:pretty;font-size:${FONT[ts]}px;${m.me ? (hc ? 'background:#000;color:#fff;' : 'background:#2F5C97;color:#fff;') : hc ? 'background:#fff;border:2px solid #000;color:#000;' : 'background:#FCFAF4;border:1px solid rgba(34,39,46,.14);color:#22272E;'}`,
                    )}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
            </div>

            <div style={st('display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:12px;')}>
              {PROMPTS.map((p) => (
                <A
                  key={p}
                  write
                  onClick={() => void say(p)}
                  s="display:flex;align-items:center;min-height:64px;padding:0 20px;border-radius:14px;font-size:20px;font-weight:600;text-decoration:none;color:#1D3F6B;background:rgba(47,92,151,.10);border:2px solid transparent;box-sizing:border-box;"
                  hover="border-color:rgba(47,92,151,.5);"
                >
                  {p}
                </A>
              ))}
            </div>

            <div style={st('font-size:19px;line-height:1.5;color:#3C454F;text-wrap:pretty;')}>
              {voice === 'listening'
                ? 'I am listening. Take your time, and say it however you like. Tap again to stop.'
                : 'Tap here, or just say “Hey Siri, talk to Atlas” or “Alexa, open Atlas” from anywhere in the room.'}
            </div>
            {voice === 'denied' && (
              <div role="alert" style={st('display:flex;align-items:flex-start;gap:14px;flex-wrap:wrap;padding:18px 20px;border-radius:16px;background:#FFF4DC;border:2px solid #8A5A00;color:#22272E;min-width:0;')}>
                <span style={st('flex:1 1 280px;min-width:0;')}>
                  <span style={st('display:block;font-size:22px;font-weight:800;line-height:1.3;')}>I cannot hear you right now.</span>
                  <span style={st('display:block;font-size:19px;line-height:1.5;margin-top:6px;text-wrap:pretty;')}>
                    Siri or Alexa is switched off on this device, or the microphone is. You can type to me in the box below, or press Call a nurse at the top. A staff member can switch it back on in the device’s own settings.
                  </span>
                </span>
                <A onClick={() => setVoice('idle')} s="flex:0 0 auto;display:inline-flex;align-items:center;min-height:60px;padding:0 24px;border-radius:12px;font-size:19px;font-weight:700;text-decoration:none;color:#fff;background:#1D3F6B;">
                  I will type
                </A>
              </div>
            )}
            <div style={st('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:12px;padding:16px;border-radius:18px;background:#FCFAF4;border:2px solid rgba(34,39,46,.14);min-width:0;')}>
              <A onClick={toggleVoice} s={`display:inline-flex;align-items:center;gap:13px;min-height:64px;padding:0 28px;border-radius:12px;text-decoration:none;font-size:21px;font-weight:700;white-space:nowrap;color:#fff;background:${VB[voice]};`}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={st('flex:0 0 28px;')} aria-hidden="true">
                  <path d="M12 3.5a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0v-5a3 3 0 0 1 3-3Z" />
                  <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
                  <path d="M12 17.5v3" />
                </svg>
                {VL[voice]}
                {voice === 'thinking' && (
                  <span style={st('display:inline-flex;align-items:center;gap:5px;height:20px;')}>
                    <span style={st('width:7px;height:7px;border-radius:50%;background:currentColor;animation:vu 1.1s ease-in-out infinite;')} />
                    <span style={st('width:7px;height:7px;border-radius:50%;background:currentColor;animation:vu 1.1s ease-in-out .2s infinite;')} />
                    <span style={st('width:7px;height:7px;border-radius:50%;background:currentColor;animation:vu 1.1s ease-in-out .4s infinite;')} />
                  </span>
                )}
                {vOn && (
                  <span style={st('display:inline-flex;align-items:flex-end;gap:3px;height:20px;')}>
                    <span style={st('width:4px;border-radius:2px;background:currentColor;animation:vu 1s ease-in-out infinite;height:8px;')} />
                    <span style={st('width:4px;border-radius:2px;background:currentColor;animation:vu 1s ease-in-out .18s infinite;height:16px;')} />
                    <span style={st('width:4px;border-radius:2px;background:currentColor;animation:vu 1s ease-in-out .36s infinite;height:11px;')} />
                  </span>
                )}
              </A>
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void say(draft);
                  }
                }}
                placeholder="Or type here…"
                aria-label="Type a message"
                style={st(`flex:1;display:flex;align-items:center;min-height:56px;padding:0 18px;border-radius:11px;min-width:0;box-sizing:border-box;font-size:${BODY[ts]}px;${hc ? 'background:#fff;border:2px solid #000;color:#22272E;' : 'background:#FCFAF4;border:2px solid rgba(34,39,46,.28);color:#54606C;'}`)}
              />
              <A write onClick={() => void say(draft)} s="display:inline-flex;align-items:center;gap:9px;min-height:60px;padding:0 26px;border-radius:12px;background:#1D3F6B;color:#fff;font-size:20px;font-weight:700;text-decoration:none;flex:0 0 auto;white-space:nowrap;" hover="background:#254A7C;">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 12h15" />
                  <path d="m13 6 6 6-6 6" />
                </svg>
                Send
              </A>
            </div>
            {err && <div role="alert" style={st('font-size:18px;color:#8A1C16;line-height:1.45;')}>{err}</div>}
            <div style={st('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:6px;')}>
              <A onClick={() => setAloud(!aloud)} selected={aloud} s="display:inline-flex;align-items:center;min-height:60px;padding:0 18px;border-radius:12px;font-size:19px;box-sizing:border-box;font-weight:600;text-decoration:none;white-space:nowrap;color:#1D3F6B;background:rgba(47,92,151,.10);">
                {aloud ? 'Reading answers out loud' : 'Answers are silent'}
              </A>
              <span style={st('flex:1 1 240px;min-width:0;display:flex;align-items:center;gap:9px;font-size:19px;font-weight:600;color:#3C454F;')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1F6B52" strokeWidth="1.8" style={st('flex:0 0 18px;')} aria-hidden="true">
                  <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
                  <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
                </svg>
                Our talks stay private. I only share what you ask me to.
              </span>
            </div>
          </div>
        )}
        {tab === 'day' && <MyDayTab />}
        {tab === 'me' && residentId && <AboutMe residentId={residentId} fullName={fullName} ts={ts} hc={hc} />}
      </div>
    </div>
  );
}
